import React, {useEffect, useState} from "react";
import {useLocation, useParams} from "react-router-dom";
import Page from "../../Components/Page";
import {makeApiCall, makeApiCallPost} from "../../../Api/makeApiCall";
import ReactTable from "../../Components/ReactTable";
import {getRetailerName} from "../../../utils/localCache/retailerMapCache";
import {SELECT_COLUMN} from "../../Components/ReactTable/CheckboxColumnComponent";
import {Breadcrumbs} from "../../Components/Breadcrumbs";
import RetailerIdAndName from "../../Components/RetailerIdAndName";
import {sortAscendingWithNullsLast} from "../../../utils/sorting";
import _ from "lodash";
import {getNicelyFormattedNumber} from "../../../utils/formatUtil";
import checkCircle from "../../../assets/check-circle.svg";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import ReactTableFilterDropdown from "../../ReactTable/ReactTableFilterDropdown";
import {redirectWithMessage} from "../../../utils/redirectMessage";
import {useQueryParams} from '../../../utils/urlBuilder';
import {qsBuilderNoNils} from './designationUtils';

const DesignationAssignStore = () => {
    const params = useParams()
    const qsParams = useQueryParams(useLocation());
    const designationId = parseInt(params.designationId)
    const version = qsParams.get('version');
    const storesEndpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/stores/`;
    const [loading, setLoading] = useState(true);
    const [tableData, setTableData] = useState({});
    const [designationDetails, setDesignationDetails] = useState({});


    const columns = [
        SELECT_COLUMN,
        {
            header: "Store",
            id: 'store',
            accessorKey: 'store',
            minSize: 300,
            filterFn: (row, columnId, filterValue) => {
                return !filterValue || Boolean(`${row.original.store.toLowerCase()}`.includes(filterValue.toLowerCase()));
            }
        },
        {
            header: "Store Tag",
            id: "storeTag",
            accessorKey: 'storeTag',
            size: 64,
            filterFn: (row, columnId, filterValue) => {
                return !filterValue || Boolean(`${row.original.storeTag}`.includes(filterValue));
            }
        },
        {
            header: "Is Closed?",
            id: "closed",
            accessorKey: 'closed',
            size: 64,
            filter: (theseProps) => {
                return <ReactTableFilterDropdown
                    id={"closed"}
                    options={[{value: "1", label: "1"}, {value: "0", label: "0"}]}
                    onChange={(selected) => theseProps.column.setFilterValue(selected)}
                    selectedValue={theseProps.column.getFilterValue()}
                    isClearable={true}
                />
            }
        },
        {
            header: "Retailer",
            id: "retailer",
            accessorKey: 'retailer',
            minSize: 100,
            cell: ({row}) => <RetailerIdAndName retailerId={row.original.retailerId}/>,
            filterFn: (row, columnId, filterValue) => {
                return !filterValue || Boolean(`${row.original.retailerId} - ${row.original.retailerName.toLowerCase()}`.includes(filterValue.toLowerCase()));
            },
            sortDescFirst: false,
            sortingFn: (rowA, rowB) => {
                const rowBValue = rowB.original.retailerId;
                const rowAValue = rowA.original.retailerId;
                return sortAscendingWithNullsLast(rowAValue, rowBValue);
            }
        },
        {
            header: "Consumer Name",
            id: "consumerName",
            accessorKey: 'consumerName'
        },
        {
            header: "City",
            id: "city",
            accessorKey: 'city'
        },
        {
            header: "Country",
            id: "country",
            accessorKey: 'country',
            size: 64,
        },
        {
            header: "State",
            id: "state",
            accessorKey: 'state',
            size: 164,
            filterFn: (row, columnId, filterValue) => {
                return _.isEmpty(filterValue) || _.includes(filterValue, row.original.state)
            },
            filter: (theseProps) => {
                const uniqueStates = _.uniqBy(theseProps.table.getCoreRowModel().rows
                    .map(row => row.original.state)
                    .filter(state => state !== '')
                    .sort())
                const statesOptionsSorted = uniqueStates.map(state => {
                    return {label: state, value: state}
                })
                return <ReactTableFilterDropdown
                    id={theseProps.column.id}
                    options={statesOptionsSorted}
                    onChange={(selected) => theseProps.column.setFilterValue(selected)}
                    selectedValues={theseProps.column.getFilterValue()}
                    isMulti
                    isClearable={true}
                    placeholder={''}
                />
            }
        },
        {
            header: "Zip Code",
            id: "zip1",
            accessorKey: 'zip1'
        }
    ]


    useEffect(() => {
        setLoading(true)
        const versionParam = version ? `?version=${version}` : ''
        Promise.all([makeApiCall(storesEndpointUrl), makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/designationDetails/${designationId}${versionParam}`)])
            .then(([storesResults, designationDetails]) => {
                setDesignationDetails(designationDetails)
                setLoading(false)
                const storeLookup = _.keyBy(designationDetails.stores, 'storeId');
                const mappedDesignationsData = storesResults.data.filter(store => !storeLookup[store.storeId])
                    .map((result) => {
                        return {
                            retailerId: result.retailerId,
                            retailerName: getRetailerName(result.retailerId.toString()),
                            storeTag: result.storeTag,
                            store: `${result.storeId} - ${result.storeName}`,
                            closed: result.closed,
                            consumerName: result.consumerName,
                            city: result.city,
                            country: result.country,
                            state: result.state,
                            zip1: result.zip1,
                            storeId: result.storeId
                        }
                    })
                setTableData(mappedDesignationsData)

            })
    }, []);

    const designation = designationDetails.designation || {};

    function submitSelectedStore(selectedRows) {
        const storesToAdd = selectedRows.map(x => x.original.storeId);
        const apiPost = {
            storeIds: storesToAdd,
            designationId: designationId,
            version
        };

        makeApiCallPost(`${process.env.REACT_APP_EDP_API_BASE_URL}/designation/addStores`, apiPost).then(() => {
            const message = `Successfully assigned ${storesToAdd.length} store/s to ${designation.index} - ${designation.name}.`;
            const redirectUrl = `/designation/${designation.index}?${qsBuilderNoNils({version})}`;
            redirectWithMessage(message, redirectUrl);
        });
    }

    return <>
        <LoadingWrapper loading={loading}>
            <Page name={`Assign store to ${designation.index} - ${designation.name}`}
                  breadCrumbs={<Breadcrumbs items={[
                      {label: "Services"},
                      {label: "Designations", to: "/designations"},
                      {label: "Designation Details ", to: `/designation/${designation.index}`},
                      {label: `${designation.name}`}
                  ]}/>}
            >
                <ReactTable
                    filterable={true}
                    data={tableData}
                    columns={columns}
                    initialState={{pagination: {pageSize: 50}}}
                >
                    {
                        ({table}) => {
                            const totalRows = getNicelyFormattedNumber(table.getFilteredRowModel().rows.length);
                            const selectedRows = table.getSelectedRowModel().rows;
                            const isAllRowsSelected = table.getIsAllRowsSelected();
                            let selectAllButtonText = `Select all ${totalRows} stores`;
                            const allRowsSelectedText = `${totalRows} stores selected`
                            return (
                                <div className={'designation-action-buttons-container'}
                                     data-testid={"action-button-container"}>
                                    {isAllRowsSelected ?
                                        <>
                                            <img alt={'status'} className={'success-image'} src={checkCircle}/>
                                            <div data-testid={'all-stores-selected'}>{allRowsSelectedText}</div>
                                        </>
                                        :
                                        <button data-testid={'select-all-stores'} type={"button"} className={"button tertiary"}
                                                onClick={() => table.toggleAllRowsSelected()}
                                        >{selectAllButtonText}</button>}
                                    <button disabled={selectedRows.length === 0} type={"button"} className={"button primary"} onClick={() => submitSelectedStore(selectedRows)}>
                                        Assign store
                                    </button>
                                </div>
                            );
                        }
                    }
                </ReactTable>
            </Page>
        </LoadingWrapper>
    </>
}

export default DesignationAssignStore;
